import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Alert,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CentralMonitoring from './CentralMonitoring';
import { openIndividualMonitoringPopupWindow } from '../components/monitoring/cctvPopupRoutes';
import { axiosInstance } from '../services/axiosInstance';
import { extractArrayFromApiPayload } from '../utils/api';

type AdminRow = {
  id: number;
  username: string;
  full_name?: string | null;
  is_superuser?: boolean;
  monitor_all_pools?: boolean;
  monitoring_pool_ids?: number[];
};

type AssignmentRow = {
  id: number;
  code?: string;
  name?: string;
  monitoring_admin_id?: number | null;
};

/**
 * 개별 관제 모니터링
 * - 일반관리자: 배당 시설만 같은 화면에서 관제 (팝업 아님)
 * - 최고관리자: 관리자 선택 후 통합관제와 같은 팝업으로 해당 관리자 배당 시설만 관제
 */
const IndividualMonitoringPage: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuper = Boolean(authState.user?.is_superuser);
  const myId = authState.user?.id != null ? Number(authState.user.id) : null;

  const adminIdFromQuery = useMemo(() => {
    const raw = searchParams.get('adminId');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const [admins, setAdmins] = useState<AdminRow[]>([]);
  /** adminId → 배당 시설 이름 목록 */
  const [poolNamesByAdmin, setPoolNamesByAdmin] = useState<Record<number, string[]>>({});
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 일반관리자: 자신의 adminId 를 쿼리에 고정 → CentralMonitoring 필터
  useEffect(() => {
    if (authState.loading) return;
    if (isSuper) return;
    if (myId == null || !Number.isFinite(myId)) return;
    if (adminIdFromQuery !== myId) {
      setSearchParams({ adminId: String(myId) }, { replace: true });
    }
  }, [authState.loading, isSuper, myId, adminIdFromQuery, setSearchParams]);

  // 최고관리자: 쿼리 없으면 관리자 선택 다이얼로그 + 배당 시설명
  useEffect(() => {
    if (authState.loading) return;
    if (!isSuper) return;
    if (adminIdFromQuery != null) return;
    setPickerOpen(true);
    setLoadingAdmins(true);
    setError(null);

    void (async () => {
      try {
        const [usersRes, assignRes] = await Promise.all([
          axiosInstance.get('/api/users/'),
          axiosInstance.get('/api/users/monitoring-assignments').catch(() => null),
        ]);

        const list = Array.isArray(usersRes.data)
          ? usersRes.data
          : extractArrayFromApiPayload(usersRes.data);
        const regularAdmins = (Array.isArray(list) ? list : []).filter(
          (a: AdminRow) => a && !a.is_superuser && Number(a.id) > 0
        );
        setAdmins(regularAdmins);

        const nameMap: Record<number, string[]> = {};
        if (assignRes?.data) {
          const assignments = normalizeAssignments(assignRes.data);
          for (const p of assignments) {
            const aid = p.monitoring_admin_id != null ? Number(p.monitoring_admin_id) : NaN;
            if (!Number.isFinite(aid) || aid <= 0) continue;
            const label = (p.name || p.code || '').trim();
            if (!label) continue;
            if (!nameMap[aid]) nameMap[aid] = [];
            if (!nameMap[aid].includes(label)) nameMap[aid].push(label);
          }
        }

        // assignments 실패·비어 있으면 pool_ids 로 pools API 보강
        const needsFallback = regularAdmins.some(
          (a) =>
            Array.isArray(a.monitoring_pool_ids) &&
            a.monitoring_pool_ids.length > 0 &&
            !(nameMap[Number(a.id)]?.length)
        );
        if (needsFallback) {
          try {
            const poolsRes = await axiosInstance.get('/api/pools/');
            const pools = extractArrayFromApiPayload(poolsRes.data) as Array<{
              id: number;
              name?: string;
              code?: string;
              monitoring_admin_id?: number | null;
            }>;
            for (const p of pools) {
              const aid =
                p.monitoring_admin_id != null ? Number(p.monitoring_admin_id) : NaN;
              if (!Number.isFinite(aid) || aid <= 0) continue;
              const label = String(p.name || p.code || '').trim();
              if (!label) continue;
              if (!nameMap[aid]) nameMap[aid] = [];
              if (!nameMap[aid].includes(label)) nameMap[aid].push(label);
            }
          } catch {
            /* ignore */
          }
        }

        setPoolNamesByAdmin(nameMap);
      } catch {
        setError('관리자 목록을 불러오기 실패');
        setAdmins([]);
        setPoolNamesByAdmin({});
      } finally {
        setLoadingAdmins(false);
      }
    })();
  }, [authState.loading, isSuper, adminIdFromQuery]);

  const handlePickAdmin = (adminId: number) => {
    setPickerOpen(false);
    openIndividualMonitoringPopupWindow(adminId);
    navigate('/central-control', { replace: true });
  };

  const formatPoolSecondary = (admin: AdminRow): string | undefined => {
    if (admin.monitor_all_pools) return '시설 전체';
    const names = poolNamesByAdmin[Number(admin.id)] || [];
    // 중복 제거 후 시설 이름만 (「배당」·아이디 반복 표시 없음)
    const unique = Array.from(
      new Set(names.map((n) => String(n).trim()).filter(Boolean))
    );
    if (unique.length > 0) {
      return unique.join(', ');
    }
    return undefined;
  };

  // 일반관리자 / (최고관리자+쿼리): CentralMonitoring
  if (!isSuper || adminIdFromQuery != null) {
    if (!isSuper && (myId == null || adminIdFromQuery == null)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    return <CentralMonitoring />;
  }

  return (
    <Dialog open={pickerOpen} onClose={() => navigate('/central-control')} maxWidth="sm" fullWidth>
      <DialogTitle>개별 관제 — 관리자 선택</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          관제할 일반관리자를 선택하면, 해당 관리자에게 배당된 시설만 팝업으로 모니터링.
        </Typography>
        {loadingAdmins ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : admins.length === 0 ? (
          <Alert severity="info">배당 가능한 일반관리자 없음.</Alert>
        ) : (
          <List dense>
            {admins.map((a) => {
              const poolSecondary = formatPoolSecondary(a);
              return (
              <ListItemButton key={a.id} onClick={() => handlePickAdmin(Number(a.id))}>
                <ListItemText
                  primary={a.full_name || a.username}
                  secondary={poolSecondary}
                  secondaryTypographyProps={
                    poolSecondary
                      ? {
                          sx: {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                        }
                      : undefined
                  }
                />
              </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate('/central-control')}>취소</Button>
      </DialogActions>
    </Dialog>
  );
};

function normalizeAssignments(raw: unknown): AssignmentRow[] {
  const list = Array.isArray(raw) ? raw : extractArrayFromApiPayload(raw);
  if (!Array.isArray(list)) return [];
  return list as AssignmentRow[];
}

export default IndividualMonitoringPage;
